import { Filter } from 'lucide-react';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import Button from 'src/componentes/button';
import Checkbox from 'src/componentes/checkbox';
import Drawer from 'src/componentes/drawer';
import InputNumber from 'src/componentes/input-number';
import Select2 from 'src/componentes/select2';
import ColaboradorEGrupoSimplificadoLookup from 'src/lookups/ColaboradorEGrupoSimplificadoLookup';
import CargoLookup from 'src/lookups/cargo';
import OrganizacaoLookup from 'src/lookups/item-organizacao';
import useAuth from 'src/hooks/useAuth';
import formatDateFns from 'src/utils/formatDateFns';
import { capitalizeFirstLetter } from 'src/utils/string';
import { getDataAplicacaoFormularioGlobal } from 'src/paginas/avaliacao-desempenho/PesquisaClima/utils';

import { PERFIL_LIDERANCA } from '../ParticipantesPesquisaClimaTab/constants';

const DashboardFiltroDrawer = ({
  aplicacoes = [],
  filtro,
  focusTrapPriority = 2,
  onAplicarFiltro,
  onChangeFiltro,
  onClearFiltro,
  onClose,
  open,
  recorrente = false,
}) => {
  const intl = useIntl();
  const { terms: resources, user } = useAuth();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma, true);
  const formatarData = (value) => {
    const data = getDataAplicacaoFormularioGlobal(value);
    return data && !Number.isNaN(data.getTime())
      ? format(data, currentDisplay, { locale: currentLocale })
      : '-';
  };
  const opcoesAplicacao = aplicacoes.map((uso) => ({
    id: uso.formularioUsoId,
    descricao: intl.formatMessage(
      { id: 'labelPeriodoAplicacaoPesquisaClima' },
      {
        dataInicio: formatarData(uso.dataInicio),
        dataTermino: formatarData(uso.dataTermino),
      }
    ),
  }));
  const perfilLiderancaLabel = intl.formatMessage({
    id: 'labelPerfilLideranca',
  });
  const perfisLideranca = [
    {
      id: PERFIL_LIDERANCA.TODOS,
      descricao: intl.formatMessage({ id: 'todos' }),
    },
    {
      id: PERFIL_LIDERANCA.LIDERES,
      descricao: intl.formatMessage({
        id: 'labelPerfilLiderancaLideres',
      }),
    },
    {
      id: PERFIL_LIDERANCA.NAO_LIDERES,
      descricao: intl.formatMessage({
        id: 'labelPerfilLiderancaNaoLideres',
      }),
    },
  ];

  return (
    <Drawer
      open={open}
      close={onClose}
      title={
        <div
          className="font-weight-bold pt-4 pb-4"
          style={{
            color: '#404040',
            fontSize: '1.25rem',
          }}
        >
          {capitalizeFirstLetter(intl.formatMessage({ id: 'filtros' }))}
        </div>
      }
      position="right"
      size={430}
      zIndex={12000}
      focusTrap
      focusTrapPriority={focusTrapPriority}
      actions={
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <button
            type="button"
            style={{
              background: 'transparent',
              border: 0,
              padding: 0,
              color: '#0D7E94',
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: 'bold',
            }}
            onClick={onClearFiltro}
          >
            {intl.formatMessage({ id: 'limparTodos' })}
          </button>
          <Button
            color="primary"
            leftIcon={Filter}
            onClick={onAplicarFiltro}
            style={{ width: '7rem' }}
          >
            {intl.formatMessage({ id: 'label.filtrar' })}
          </Button>
        </div>
      }
      actionsStyle={{ borderTop: '1px solid #e4eaec', padding: 20 }}
      bodyStyle={{ overflowX: 'hidden', overflowY: 'auto' }}
      headerStyle={{
        borderBottom: '1px solid #e4eaec',
        paddingBottom: 0,
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 0,
      }}
    >
      <div>
        <Row>
          {recorrente && (
            <Col md={12}>
              <Select2
                clearable={false}
                labelKey="descricao"
                valueKey="id"
                options={opcoesAplicacao}
                model={{
                  label: intl.formatMessage({ id: 'labelAplicacaoDashboard' }),
                  value: opcoesAplicacao.find(
                    (uso) => uso.id === filtro.formularioUsoId
                  ),
                  requestChange: (uso) =>
                    onChangeFiltro('formularioUsoId', uso?.id ?? null),
                }}
              />
            </Col>
          )}
          <Col md={12}>
            <InputNumber
              min={0}
              max={10000}
              model={{
                label: intl.formatMessage(
                  { id: 'tempoCargoDias' },
                  { cargo: resources.cargo }
                ),
                value: filtro.tempoCargoDias,
                requestChange: (value) =>
                  onChangeFiltro('tempoCargoDias', value),
              }}
            />
          </Col>
          <Col md={12}>
            <InputNumber
              min={0}
              max={10000}
              model={{
                label: intl.formatMessage({ id: 'tempoEmpresaDias' }),
                value: filtro.tempoEmpresaDias,
                requestChange: (value) =>
                  onChangeFiltro('tempoEmpresaDias', value),
              }}
            />
          </Col>
          <Col md={12}>
            <CargoLookup
              clearable
              compactMulti
              multi
              model={{
                label: capitalizeFirstLetter(resources.cargos),
                value: filtro.cargos,
                requestChange: (value) => onChangeFiltro('cargos', value),
              }}
            />
          </Col>
          <Col md={12}>
            <OrganizacaoLookup
              clearable
              compactMulti
              multi
              model={{
                label: capitalizeFirstLetter(resources.areas),
                value: filtro.areas,
                requestChange: (value) => onChangeFiltro('areas', value),
              }}
            />
          </Col>
          <Col md={12}>
            <Checkbox
              className="pb-2"
              disabled={!filtro.areas?.length}
              model={{
                label: intl.formatMessage(
                  {
                    id: 'label.considerarAreasSubordinadas',
                  },
                  { areas: resources.areas }
                ),
                value: filtro.considerarAreasSubordinadas,
                requestChange: (value) =>
                  onChangeFiltro('considerarAreasSubordinadas', value),
              }}
            />
          </Col>
          <Col md={12}>
            <Select2
              aria-label={perfilLiderancaLabel}
              clearable={false}
              labelKey="descricao"
              valueKey="id"
              options={perfisLideranca}
              showHint
              idHint="perfil-lideranca-dashboard-pesquisa-clima"
              labelHint={intl.formatMessage(
                {
                  id: 'hintPerfilLiderancaPesquisaClima',
                },
                {
                  pesquisa: resources.pesquisa,
                  colaboradores: resources.colaboradores,
                  colaborador: resources.colaborador,
                }
              )}
              model={{
                label: perfilLiderancaLabel,
                value: perfisLideranca.find(
                  (perfil) => perfil.id === filtro.perfilLideranca
                ),
                requestChange: (perfil) =>
                  onChangeFiltro(
                    'perfilLideranca',
                    perfil?.id ?? PERFIL_LIDERANCA.TODOS
                  ),
              }}
            />
          </Col>
          <Col md={12}>
            <ColaboradorEGrupoSimplificadoLookup
              abrirEmDrawer
              labelComoTitulo
              compactMulti
              multi
              clearable
              data={{ ocultarInativos: true, somenteAtivos: true }}
              model={{
                label: intl.formatMessage({
                  id: 'labelLideradosDe',
                }),
                value: filtro.lideradosDe,
                requestChange: (value) => onChangeFiltro('lideradosDe', value),
              }}
            />
          </Col>
          <Col md={12}>
            <Checkbox
              className="pb-2"
              disabled={!filtro.lideradosDe?.length}
              model={{
                label: intl.formatMessage({
                  id: 'labelLideradosDiretos',
                }),
                value: filtro.lideradosDiretos,
                requestChange: (value) =>
                  onChangeFiltro('lideradosDiretos', value),
              }}
            />
          </Col>
          <Col md={12}>
            <Checkbox
              className="pb-2"
              disabled={!filtro.lideradosDe?.length}
              model={{
                label: intl.formatMessage({
                  id: 'labelLideradosIndiretos',
                }),
                value: filtro.lideradosIndiretos,
                requestChange: (value) =>
                  onChangeFiltro('lideradosIndiretos', value),
              }}
            />
          </Col>
        </Row>
      </div>
    </Drawer>
  );
};

export default DashboardFiltroDrawer;
